// src/components/HeroSection.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';

const SLIDES = [
  { src: '/images/sai_hero_1.png', label: 'Luxury Residences' },
  { src: '/images/sai_hero_2.png',     label: 'Commercial Spaces'  },
  { src: '/images/sai_hero_3.png',     label: 'Plot Investments'   },
  { src: '/images/sai_hero_4.png',     label: 'Premium Villas'     },
  { src: '/images/sai_hero_5.png',     label: 'Rental Properties'  },
];

const SLIDE_DURATION = 4000;

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [progKey, setProgKey] = useState(0);
  const timerRef = useRef(null);

  const goTo = useCallback((n) => {
    clearInterval(timerRef.current);
    setCurrent((n + SLIDES.length) % SLIDES.length);
    setProgKey(k => k + 1);
  }, []);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, SLIDE_DURATION);
    return () => clearInterval(timerRef.current);
  }, [next]);

  return (
    <div style={{ background: '#0b1220', fontFamily: "'Inter', sans-serif" }}>

      {/* ── MAIN HERO ─────────────────────────────────────────────────────── */}
      <div className="hero-split flex w-full overflow-hidden"
           style={{ height: '75vh', minHeight: '480px', maxHeight: '560px' }}>

        {/* ══ LEFT PANEL — 30% ══════════════════════════════════════════════ */}
        <div className="left-panel-w relative flex flex-col justify-center flex-shrink-0"
             style={{
               width: '30%', minWidth: '220px',
               background: '#fbfbfbff',
               padding: 'clamp(1.2rem,2vw,2rem) clamp(1rem,1.8vw,1.8rem)',
               zIndex: 5,
             }}>

          {/* Right-edge gold accent line */}
          <div className="absolute top-0 right-0 w-px h-full"
               style={{ background: 'linear-gradient(to bottom,transparent,rgba(196,165,90,.35) 30%,rgba(196,165,90,.35) 70%,transparent)' }} />

          {/* Tag */}
          <div className="flex items-center gap-2 mb-4">
            <div className="h-px bg-[#c4a55a]/35" style={{ width: '22px' }} />
            <span className="text-[8px] font-bold tracking-[.22em] uppercase text-[#c4a55a]">
              Your Trusted Property Partner
            </span>
          </div>

          {/* Brand */}
         <div style={{ fontFamily: "'Cormorant Garamond', serif", lineHeight: 1, marginBottom: '.5rem', textAlign: 'center' }}>
  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'center' }}>
    <span style={{ fontSize: 'clamp(42px,5.5vw,64px)', fontWeight: 700, color: '#0c0c0cff', letterSpacing: '2px' }}>
      Shree
    </span>
    <span style={{ fontSize: 'clamp(42px,5.5vw,64px)', fontWeight: 700, color: '#0e0367ff', letterSpacing: '2px' }}>
      SAI
    </span>
  </div>
  <span style={{ display: 'block', fontSize: 'clamp(42px,5.5vw,64px)', fontWeight: 700, color: 'rgba(51, 50, 50, 0.65)', letterSpacing: '2px', marginTop: '2px' }}>
    PROPERTIES
  </span>
</div>

          {/* Gold rule */}
          <div className="w-full h-px bg-[#c4a55a]/50 my-3" />

          {/* Services */}
          <p className="text-[10px] font-bold tracking-[.18em] uppercase mb-3"
             style={{ color:'rgba(5, 5, 5, 0.33)', lineHeight:'1.9' }}>
            Rent · Sale · Buy<br/>Agreements · Documents · Dealers
          </p>

          {/* Headline */}
          <h1 className="font-extrabold tracking-[.07em] uppercase leading-snug text-black mb-4"
              style={{ fontSize:'clamp(11px,1.3vw,13px)' }}>
            Pune's Most Trusted<br/>Real Estate Network
          </h1>

          {/* Pills */}
          <div className="flex flex-row gap-1.5 mb-4">
            {[
              { icon: <MapPin size={10} color="#c4a55a" />, text: 'Pune, Maharashtra' },
              { icon: <ShieldCheck size={10} color="#c4a55a" />, text: 'Trusted Since 2012' },
            ].map((p, i) => (
              <div key={i} className="flex items-center gap-1.5 px-2.5 py-[5px] border border-[#c4a55a]/28"
                   style={{ background:'rgba(196,165,90,.08)', fontSize:'8px', fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'#c4a55a' }}>
                {p.icon}{p.text}
              </div>
            ))}
          </div>

          {/* CTAs */}
          <Link to="/inquiry"
            className="block w-full text-center py-2.5 mb-2 font-extrabold tracking-[.2em] uppercase transition-opacity hover:opacity-85"
            style={{ background:'#c4a55a', color:'#0b1220', fontSize:'9px' }}>
            Start Your Journey
          </Link>
          <Link to="/rent"
            className="block w-full text-center py-2 font-bold tracking-[.2em] uppercase text-black border border-black/25 hover:border-white/60 transition-colors"
            style={{ fontSize:'9px' }}>
            Explore Properties
          </Link>

          {/* Slide counter */}
          <div className="absolute bottom-4 left-5 text-[11px] tracking-[.1em]"
               style={{ fontFamily:"'Cormorant Garamond',serif", color:'rgba(196,165,90,.5)' }}>
            {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
          </div>
        </div>

        {/* ══ RIGHT PANEL — 70% (slideshow) ═════════════════════════════════ */}
        <div className="right-panel-h relative flex-1 overflow-hidden">

          {/* Progress bar */}
          <div key={progKey} className="absolute top-0 left-0 h-[2px] z-20"
               style={{ background:'#c4a55a', animation:`slideProgress ${SLIDE_DURATION}ms linear forwards` }} />

          {/* Images */}
          {SLIDES.map((slide, i) => (
            <img key={slide.src} src={slide.src} alt={slide.label}
                 className="absolute inset-0 w-full h-full object-cover"
                 style={{
                   opacity: i === current ? 1 : 0,
                   transform: i === current ? 'scale(1)' : 'scale(1.05)',
                   transition: 'opacity 1s ease, transform 6s ease',
                   zIndex: 0,
                 }} />
          ))}

          {/* Left-edge gradient blending into the panel */}
          {/* <div className="absolute inset-0 pointer-events-none z-[2]"
               style={{ background:'linear-gradient(to right,rgba(11,18,32,.6) 0%,rgba(11,18,32,0) 35%)' }} /> */}


          {/* Rotating key icon */}
          <div className="absolute top-[12%] left-[3%] z-[4] opacity-30"
               style={{ animation:'spinSlow 14s linear infinite' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c4a55a" strokeWidth="1.2">
              <circle cx="8" cy="15" r="5"/>
              <path d="m21 3-9.5 9.5M15 9l3 3"/>
              <path d="M6 15h.01"/>
            </svg>
          </div>

          {/* Arrow buttons - Hide on mobile */}
          <button onClick={prev}
            className="hero-arrows absolute left-2.5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center border border-[#c4a55a]/30 hover:bg-[#c4a55a]/20 transition-colors"
            style={{ background:'rgba(11,18,32,.5)', color:'#c4a55a' }} aria-label="Previous slide">
            <ChevronLeft size={16} />
          </button>
          <button onClick={next}
            className="hero-arrows absolute right-2.5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center border border-[#c4a55a]/30 hover:bg-[#c4a55a]/20 transition-colors"
            style={{ background:'rgba(11,18,32,.5)', color:'#c4a55a' }} aria-label="Next slide">
            <ChevronRight size={16} />
          </button>

          {/* Bottom label + dots */}
          <div className="absolute bottom-0 left-0 right-0 z-10 flex items-end justify-between px-4 pb-4 pt-12"
               style={{ background:'linear-gradient(to top,rgba(11,18,32,.82) 0%,transparent 100%)' }}>
            <span className="text-[10px] font-bold tracking-[.18em] uppercase"
                  style={{ color:'rgba(196,165,90,.8)' }}>
              {SLIDES[current].label}
            </span>
            <div className="flex gap-1.5 items-center">
              {SLIDES.map((_, i) => (
                <button key={i} onClick={() => goTo(i)}
                  className="transition-all rounded-full"
                  style={{
                    width: i === current ? '18px' : '5px',
                    height: '5px',
                    borderRadius: i === current ? '3px' : '50%',
                    background: i === current ? '#c4a55a' : 'rgba(196,165,90,.3)',
                    border: 'none', cursor: 'pointer', padding: 0,
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-[#c4a55a]/18" style={{ background:'#0b1220' }}>
        {[
          { num:'500+', label:'Happy Clients'   },
          { num:'200+', label:'Properties Sold' },
          { num:'50+',  label:'Trusted Dealers' },
          { num:'10+',  label:'Years Experience'},
        ].map((s, i) => (
          <div key={i} className="text-center py-3 border-r border-[#c4a55a]/10 last:border-r-0 sm:border-r sm:last:border-r-0">
            <div className="font-bold text-[#c4a55a]"
                 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'22px', lineHeight:1 }}>
              {s.num}
            </div>
            <div className="text-[8px] font-bold tracking-[.16em] uppercase mt-1"
                 style={{ color:'rgba(255,255,255,.3)' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── BOTTOM CTA ────────────────────────────────────────────────────── */}
      <Link to="/inquiry"
        className="block w-full text-center py-4 transition-opacity hover:opacity-90"
        style={{ background:'#c4a55a' }}>
        <span className="text-[11px] font-extrabold tracking-[.25em] uppercase"
              style={{ color:'#0b1220' }}>
          Enquire Now — Call +91 97646 46044
        </span>
      </Link>

      {/* ── KEYFRAMES ─────────────────────────────────────────────────────── */}
      <style>{`
        @keyframes floatY {
          0%,100% { transform: translateY(0);   }
          50%      { transform: translateY(-7px); }
        }
        @keyframes spinSlow {
          to { transform: rotate(360deg); }
        }
        @keyframes slideProgress {
          from { width: 0%;   }
          to   { width: 100%; }
        }

        /* ── Responsive: stack vertically on mobile ── */
        @media (max-width: 768px) {
          .hero-split {
            flex-direction: column !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
          }
          
          .left-panel-w {
            width: 100% !important;
            min-width: unset !important;
            border-bottom: 1px solid rgba(196,165,90,.2);
            padding: 1.5rem 1.2rem !important;
          }
          
          .right-panel-h {
            height: 300px !important;
            min-height: 300px !important;
          }
          
          .hero-arrows {
            display: none !important;
          }
        }

        @media (max-width: 480px) {
          .right-panel-h {
            height: 240px !important;
            min-height: 240px !important;
          }
          
          .left-panel-w {
            padding: 1rem 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}